/**
 * Gestione etichette condivise (Task 9): CRUD del registro tag email dal
 * pannello impostazioni, montato in AccountSettings SOLO con il permesso
 * email.etichette_gestione (la pagina lo verifica prima di renderizzare).
 *
 * A differenza del picker/sidebar (solo attive), qui la lista include anche
 * le disattivate: servono per poterle rinominare/ricolorare o riattivare
 * esplicitamente senza doverle ricreare da zero (mostrate in stile attenuato
 * con azione "Riattiva" al posto di modifica/disattiva).
 *
 * Feedback operazioni: nessun toast globale montato nell'app (sonner e'
 * esportato da @floraltech/ui ma senza <Toaster/> nel root, quindi
 * toast.success() non apparirebbe a schermo). Si riusa lo stesso pattern
 * inline di AccountSettings/RegolePage: messaggio di errore in rosso,
 * messaggio di esito positivo in verde sotto il form.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Check, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { Badge, Button, Card, Input, Label } from '@floraltech/ui';
import {
  aggiornaEtichetta,
  creaEtichetta,
  disattivaEtichetta,
  listaEtichette,
  type EmailEtichetta,
} from '../lib/api';
import { ETICHETTE_PALETTE } from '../lib/etichetteColori';

interface FormValori {
  nome: string;
  colore: string;
  ordine: number;
}

const FORM_VUOTO: FormValori = { nome: '', colore: ETICHETTE_PALETTE[0], ordine: 0 };

/** Riga di swatch della palette fissa: non e' un dropdown, non serve ricerca. */
function SelettorePalette({
  valore,
  onChange,
}: {
  valore: string;
  onChange: (colore: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Colore etichetta">
      {ETICHETTE_PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={`Colore ${c}`}
          aria-pressed={valore === c}
          onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full transition ${
            valore === c ? 'ring-2 ring-foreground ring-offset-2 ring-offset-background' : ''
          }`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );
}

export function EtichetteManager() {
  const [etichette, setEtichette] = useState<EmailEtichetta[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [messaggio, setMessaggio] = useState<string | null>(null);
  const [salvataggio, setSalvataggio] = useState(false);

  const [mostraForm, setMostraForm] = useState(false);
  const [nuova, setNuova] = useState<FormValori>(FORM_VUOTO);

  const [editId, setEditId] = useState<string | null>(null);
  const [editValori, setEditValori] = useState<FormValori>(FORM_VUOTO);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      setEtichette(await listaEtichette(true));
    } catch {
      setErrore('Errore di rete');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  const azzeraMessaggi = () => {
    setErrore(null);
    setMessaggio(null);
  };

  const leggiErrore = async (res: Response) => {
    const body = await res.json().catch(() => null);
    return typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`;
  };

  const crea = async () => {
    if (!nuova.nome.trim()) {
      setErrore("Il nome dell'etichetta e' obbligatorio");
      return;
    }
    setSalvataggio(true);
    azzeraMessaggi();
    try {
      const res = await creaEtichetta({
        nome: nuova.nome.trim(),
        colore: nuova.colore,
        ordine: nuova.ordine,
      });
      if (!res.ok) {
        setErrore(await leggiErrore(res));
        return;
      }
      // 200 = riattivata implicitamente (nome coincidente con una disattivata),
      // 201 = creata ex-novo: il backend distingue con lo status code.
      setMessaggio(res.status === 200 ? 'Etichetta esistente riattivata' : 'Etichetta creata');
      setNuova(FORM_VUOTO);
      setMostraForm(false);
      await carica();
    } catch {
      setErrore('Errore di rete');
    } finally {
      setSalvataggio(false);
    }
  };

  const apriModifica = (e: EmailEtichetta) => {
    azzeraMessaggi();
    setEditId(e.id);
    setEditValori({ nome: e.nome, colore: e.colore, ordine: e.ordine });
  };

  const annullaModifica = () => {
    setEditId(null);
    setEditValori(FORM_VUOTO);
  };

  const salvaModifica = async () => {
    if (!editId) return;
    if (!editValori.nome.trim()) {
      setErrore("Il nome dell'etichetta e' obbligatorio");
      return;
    }
    setSalvataggio(true);
    azzeraMessaggi();
    try {
      const res = await aggiornaEtichetta(editId, {
        nome: editValori.nome.trim(),
        colore: editValori.colore,
        ordine: editValori.ordine,
      });
      if (!res.ok) {
        setErrore(await leggiErrore(res));
        return;
      }
      setMessaggio('Etichetta aggiornata');
      annullaModifica();
      await carica();
    } catch {
      setErrore('Errore di rete');
    } finally {
      setSalvataggio(false);
    }
  };

  const disattiva = async (e: EmailEtichetta) => {
    if (
      !window.confirm(
        `Disattivare l'etichetta "${e.nome}"? Restera' sui messaggi gia' etichettati ma non sara' piu' proponibile per nuovi utilizzi.`
      )
    ) {
      return;
    }
    azzeraMessaggi();
    const res = await disattivaEtichetta(e.id);
    if (!res.ok) {
      setErrore(await leggiErrore(res));
      return;
    }
    setMessaggio('Etichetta disattivata');
    await carica();
  };

  const riattiva = async (e: EmailEtichetta) => {
    azzeraMessaggi();
    const res = await aggiornaEtichetta(e.id, { attiva: true });
    if (!res.ok) {
      setErrore(await leggiErrore(res));
      return;
    }
    setMessaggio('Etichetta riattivata');
    await carica();
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Etichette condivise</h2>
        <Button
          size="sm"
          onClick={() => {
            azzeraMessaggi();
            if (mostraForm) {
              setNuova(FORM_VUOTO);
            }
            setMostraForm((v) => !v);
          }}
        >
          {mostraForm ? (
            'Chiudi'
          ) : (
            <>
              <Plus className="mr-1 h-4 w-4" />
              Nuova etichetta
            </>
          )}
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Registro dei tag applicabili ai messaggi, condiviso da tutti gli utenti che accedono alla
        posta.
      </p>

      {mostraForm && (
        <div className="mt-3 space-y-3 rounded-md border p-3">
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div>
              <Label htmlFor="et-nome">Nome</Label>
              <Input
                id="et-nome"
                value={nuova.nome}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNuova((v) => ({ ...v, nome: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="et-ordine">Ordine</Label>
              <Input
                id="et-ordine"
                type="number"
                className="w-20"
                value={nuova.ordine}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNuova((v) => ({ ...v, ordine: Number(e.target.value) }))
                }
              />
            </div>
          </div>
          <div>
            <Label>Colore</Label>
            <SelettorePalette
              valore={nuova.colore}
              onChange={(colore) => setNuova((v) => ({ ...v, colore }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMostraForm(false);
                setNuova(FORM_VUOTO);
              }}
              disabled={salvataggio}
            >
              Annulla
            </Button>
            <Button size="sm" onClick={crea} disabled={salvataggio}>
              {salvataggio ? 'Creazione...' : 'Crea etichetta'}
            </Button>
          </div>
        </div>
      )}

      {errore && <p className="mt-2 text-sm text-destructive">{errore}</p>}
      {messaggio && (
        <p className="mt-2 text-sm text-green-700 dark:text-green-400">{messaggio}</p>
      )}

      {loading && <p className="mt-3 text-sm text-muted-foreground">Caricamento...</p>}
      {!loading && etichette.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground">Nessuna etichetta.</p>
      )}

      <ul className="mt-3 divide-y">
        {etichette.map((e) => (
          <li
            key={e.id}
            className={`flex items-center gap-3 py-3 ${e.attiva ? '' : 'opacity-50'}`}
          >
            {editId === e.id ? (
              <>
                <div className="flex flex-1 flex-wrap items-center gap-3">
                  <SelettorePalette
                    valore={editValori.colore}
                    onChange={(colore) => setEditValori((v) => ({ ...v, colore }))}
                  />
                  <Input
                    className="max-w-xs"
                    value={editValori.nome}
                    aria-label="Nome etichetta"
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                      setEditValori((v) => ({ ...v, nome: ev.target.value }))
                    }
                  />
                  <Input
                    type="number"
                    className="w-20"
                    aria-label="Ordine etichetta"
                    value={editValori.ordine}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                      setEditValori((v) => ({ ...v, ordine: Number(ev.target.value) }))
                    }
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={salvaModifica}
                  disabled={salvataggio}
                  aria-label={`Salva ${e.nome}`}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={annullaModifica}
                  disabled={salvataggio}
                  aria-label="Annulla modifica"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <div className="flex flex-1 items-center gap-2">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: e.colore }}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-medium">{e.nome}</span>
                  {!e.attiva && <Badge variant="outline">disattivata</Badge>}
                  <span className="text-xs text-muted-foreground">ordine {e.ordine}</span>
                </div>
                {e.attiva ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => apriModifica(e)}
                      aria-label={`Modifica ${e.nome}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => disattiva(e)}
                      aria-label={`Disattiva ${e.nome}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => riattiva(e)}>
                    <RotateCcw className="mr-1 h-4 w-4" />
                    Riattiva
                  </Button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
